import _defineProperty from "/vercel/path0/node_modules/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

/**
 * @typedef {import('react').ReactNode} ReactNode
 * @typedef {import('mdx/types').MDXComponents} Components
 *
 * @typedef Props
 *   Configuration.
 * @property {Components} [components]
 *   Mapping of names for JSX components to React components.
 * @property {boolean} [disableParentContext=false]
 *   Turn off outer component context.
 * @property {ReactNode} [children]
 *   Children.
 *
 * @callback MergeComponents
 * @param {Components} currentComponents
 *   Current components from the context.
 * @returns {Components}
 *   Merged components.
 */
import React from 'react';
/**
 * @type {import('react').Context<Components>}
 * @deprecated
 *   This export is marked as a legacy feature.
 *   That means it’s no longer recommended for use as it might be removed
 *   in a future major release.
 *
 *   Please use `useMDXComponents` to get context based components and
 *   `MDXProvider` to set context based components instead.
 */

export var MDXContext = React.createContext({});
/**
 * @param {import('react').ComponentType<any>} Component
 * @deprecated
 *   This export is marked as a legacy feature.
 *   That means it’s no longer recommended for use as it might be removed
 *   in a future major release.
 *
 *   Please use `useMDXComponents` to get context based components instead.
 */

export function withMDXComponents(Component) {
  return boundMDXComponent;
  /**
   * @param {Record<string, unknown> & {components?: Components}} props
   * @returns {JSX.Element}
   */

  function boundMDXComponent(props) {
    var allComponents = useMDXComponents(props.components);
    return React.createElement(Component, _objectSpread(_objectSpread({}, props), {}, {
      allComponents: allComponents
    }));
  }
}
/**
 * Get current components from the MDX Context.
 *
 * @param {Components|MergeComponents} [components]
 *   Additional components to use or a function that takes the current
 *   components and filters/merges/changes them.
 * @returns {Components}
 *   Current components.
 */

export function useMDXComponents(components) {
  var contextComponents = React.useContext(MDXContext); // Memoize to avoid unnecessary top-level context changes

  return React.useMemo(function () {
    // Custom merge via a function prop
    if (typeof components === 'function') {
      return components(contextComponents);
    }

    return _objectSpread(_objectSpread({}, contextComponents), components);
  }, [contextComponents, components]);
}
/** @type {Components} */

var emptyObject = {};
/**
 * Provider for MDX context
 *
 * @param {Props} props
 * @returns {JSX.Element}
 */

export function MDXProvider(_ref) {
  var components = _ref.components,
      children = _ref.children,
      disableParentContext = _ref.disableParentContext;
  var allComponents = useMDXComponents(components);

  if (disableParentContext) {
    allComponents = components || emptyObject;
  }

  return React.createElement(MDXContext.Provider, {
    value: allComponents
  }, children);
}